import React, { useState, useEffect } from "react";

/*
 * 21 CFR Part 11 / EU Annex 11 e-signature capture, for both section and whole-document
 * approval. Layout matches the SR approver signature modal: Action + UTC clock, empty
 * username/password (no autofill), role required (read-only from profile Position).
 */

// Chrome's password-manager suggestion UI only attaches to type="password". Masking a
// plain type="text" field with -webkit-text-security avoids that; Firefox falls back to
// a real password field with the readonly-until-focus trick.
const SUPPORTS_TEXT_MASK = typeof CSS !== "undefined" && CSS.supports("-webkit-text-security", "disc");

const formatUtcDateTime = () => {
    try {
        return new Date().toLocaleString("en-GB", {
            timeZone: "UTC",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch (e) {
        const d = new Date();
        return `${String(d.getUTCDate()).padStart(2, "0")} ${d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" })} ${d.getUTCFullYear()}, ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }
};

const SignatureModal = ({ title, statement, compareContent, signerName, signerRole, signerEmail, busy, error, onSign, onClose }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [userLocked, setUserLocked] = useState(true);
    const [pwLocked, setPwLocked] = useState(true);
    const [nowUtc, setNowUtc] = useState(formatUtcDateTime);

    const hasRole = !!(signerRole && String(signerRole).trim());
    const canSign = hasRole && username.trim().length > 0 && password.length > 0 && !busy;
    const blockedReason = busy ? null
        : !hasRole ? "Your profile position (role / title) must be set before you can sign."
        : (!username.trim() && !password) ? "Enter your username and password to sign."
        : !username.trim() ? "Enter your username to sign."
        : !password ? "Enter your password to sign."
        : null;

    useEffect(() => {
        setNowUtc(formatUtcDateTime());
        const timer = setInterval(() => setNowUtc(formatUtcDateTime()), 1000);
        return () => clearInterval(timer);
    }, []);

    const submit = () => {
        if (!canSign) return;
        onSign(username.trim(), password);
    };

    return (
        <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="td-modal" style={{ maxWidth: compareContent ? 820 : 560 }}>
                <div className="td-modal-h">
                    <h3><i className="fas fa-signature" /> {title}</h3>
                    <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
                </div>
                <div className="td-modal-b">
                    {compareContent && (
                        <div style={{ maxHeight: "34vh", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 16px", marginBottom: 16, background: "#f7fafc" }}>
                            <div className="td-panel-h" style={{ marginTop: 0 }}>What you're signing</div>
                            {compareContent}
                        </div>
                    )}
                    <div className="td-statement">{statement}</div>

                    <div className="td-row2">
                        <div className="td-field">
                            <label>FULL NAME</label>
                            <input className="val" value={signerName || ""} readOnly disabled autoComplete="off" />
                        </div>
                        <div className="td-field">
                            <label>ROLE / TITLE</label>
                            <input className="val" value={signerRole || ""} placeholder="Not set on your profile" readOnly disabled autoComplete="off" />
                        </div>
                    </div>
                    <div className="td-row2">
                        <div className="td-field">
                            <label>ACTION</label>
                            <input className="val" value="Approval" readOnly disabled autoComplete="off" />
                        </div>
                        <div className="td-field">
                            <label>DATE &amp; TIME (UTC)</label>
                            <input className="val" value={nowUtc} readOnly disabled autoComplete="off" />
                        </div>
                    </div>

                    <div className="td-sig-creds">
                        <div className="td-field">
                            <label>USERNAME</label>
                            <input
                                className="val"
                                type="text"
                                value={username}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                placeholder="Type your username"
                                disabled={!hasRole || busy}
                                readOnly={userLocked}
                                onFocus={() => setUserLocked(false)}
                                onChange={(e) => setUsername(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && canSign) submit(); }}
                            />
                        </div>
                        <div className="td-field">
                            <label>PASSWORD</label>
                            <input
                                className="val"
                                type={SUPPORTS_TEXT_MASK ? "text" : "password"}
                                style={SUPPORTS_TEXT_MASK ? { WebkitTextSecurity: "disc" } : undefined}
                                value={password}
                                autoComplete={SUPPORTS_TEXT_MASK ? "off" : "new-password"}
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                placeholder="Type your password"
                                disabled={!hasRole || busy}
                                readOnly={!SUPPORTS_TEXT_MASK && pwLocked}
                                onFocus={() => setPwLocked(false)}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && canSign) submit(); }}
                            />
                        </div>
                    </div>

                    {!hasRole && (
                        <div className="td-err" style={{ marginTop: 8 }}>
                            Your profile position (role / title) must be set before you can sign.
                        </div>
                    )}
                    {error && <div className="td-err" style={{ marginTop: 8 }}>{error}</div>}
                </div>
                <div className="td-modal-f">
                    <span className="caption">Signature captured per 21 CFR Part 11 / EU Annex 11 requirements.</span>
                    <div style={{ display: "flex", gap: 10 }}>
                        <button className="td-btn td-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                        <button className="td-btn td-btn-approve" disabled={!canSign} onClick={submit} title={blockedReason}>
                            {busy ? "Signing…" : "Sign & Approve"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignatureModal;
